/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, useContext, useState } from 'react';
import sendMessageToAI, { MessagePayload, OriginalPromptData, SendMessagePayload } from '../../../domain/use_cases/scenario_chat/sendMessageToAI';
import AuthenticationContext from '../auth/AuthenticationContext';
import { ESmsRole } from '@leedpulse-monorepo/enums';
import { JobType, ContextStatus } from "@leedpulse-monorepo/enums";

interface ScenarioChatContextProps {
  items: MessagePayload[];
  originalPrompts: OriginalPromptData[];
  sendMessage: (payload: SendMessagePayload) => Promise<void>;
  reset: () => void;
  status: ContextStatus;
}

const ScenarioChatContext = createContext<ScenarioChatContextProps>({
  items: [] as MessagePayload[],
  originalPrompts: [] as OriginalPromptData[],
  sendMessage: async (payload: SendMessagePayload) => { },
  reset: () => { },
  status: ContextStatus.IDLE
});

export const ScenarioChatProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [items, setItems] = useState([] as MessagePayload[]);
  const [originalPrompts, setOriginalPrompts] = useState([] as OriginalPromptData[]);
  const [status, setStatus] = useState(ContextStatus.IDLE);

  const authContext = useContext(AuthenticationContext);

  const sendMessage = async (payload: SendMessagePayload) => {
    if (!authContext.authentication) {
      return;
    }

    setStatus(ContextStatus.LOADING);
    try {
      const messages = [...items];
      if (payload.sResponseType === JobType.reply && payload.sUserText) {
        const lastMessage = messages[messages.length - 1];
        messages.push(
          {
            created: Date.now(),
            role: ESmsRole.user,
            message: payload.sUserText,
            qualification: lastMessage.qualification
          }
        )
        setItems(messages);
      }
      const useCaseResponse = await sendMessageToAI(payload, authContext.authentication);
      const responseData = useCaseResponse.data;
      if (responseData) {
        const replyMessage = responseData.response;
        const originalPromptMessage = responseData.original_prompt;
        const qualificationAi = responseData.qualification_ai;
        messages.push(
          {
            created: Date.now(),
            role: ESmsRole.assistant,
            message: replyMessage,
            qualification: qualificationAi.qualification
          }
        )
        setItems(messages);
        setOriginalPrompts(originalPromptMessage);
        setStatus(ContextStatus.SUCCESS);
      } else {
        setStatus(ContextStatus.ERROR);
      }
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const reset = () => {
    setItems([]);
    setOriginalPrompts([]);
    setStatus(ContextStatus.IDLE);
  };

  return (
    <ScenarioChatContext.Provider
      value={{
        items,
        originalPrompts,
        sendMessage,
        reset,
        status
      }}
    >
      {children}
    </ScenarioChatContext.Provider>
  );
};

export default ScenarioChatContext;
